// Horario del GPS de los choferes: la franja de driver_tracking_config (ver
// server/current/driver/gpsPolicy.js). Fuera de la franja el servidor descarta
// las posiciones y la app apaga el seguimiento, salvo que el chofer tenga un
// recorrido cargado o en curso, o un Turbo asignado. Una sola franja, en hora
// de Buenos Aires y todos los días; si «desde» es mayor que «hasta», cruza la
// medianoche. Va en Choferes en vivo, que ya pide db_access como el resto del
// GPS (el servidor también lo exige).

import React, { useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { smallMutedStyle, subtitleStyle } from "../ui/pageStyles";
import { requestJson } from "../../lib/requestJson";

export type GpsFranja = { activo: boolean; desde: string; hasta: string; updated_by?: string | null; updated_at?: string | null };

const PATH = "/v2/driver-locations/config";
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const tituloStyle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "4px", fontSize: "13px", color: "var(--text-secondary)" };
const timeStyle: React.CSSProperties = { minHeight: "36px", padding: "0 10px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", font: "inherit", width: "130px" };
const fmtDate = (iso: string) => new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function GpsFranjaCard({ token }: { token?: string | null }) {
  const [guardada, setGuardada] = useState<GpsFranja | null>(null);
  const [form, setForm] = useState<GpsFranja | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    requestJson(PATH, { token })
      .then((json) => {
        if (!json?.config) throw new Error("No se pudo leer el horario del GPS");
        if (vivo) { setGuardada(json.config); setForm(json.config); }
      })
      .catch((e: any) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [token]);

  const cambiar = (c: Partial<GpsFranja>) => {
    setForm((f) => (f ? { ...f, ...c } : f));
    setNotice(null);
  };

  const guardar = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const json = await requestJson(PATH, { token, method: "PUT", body: { activo: form.activo, desde: form.desde, hasta: form.hasta } });
      setGuardada(json.config);
      setForm(json.config);
      setNotice("Horario guardado");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const formato = !!form && HHMM.test(form.desde) && HHMM.test(form.hasta);
  const iguales = !!form && form.desde === form.hasta;
  const cambiado = !!form && !!guardada && (form.activo !== guardada.activo || form.desde !== guardada.desde || form.hasta !== guardada.hasta);

  return (
    <Card>
      <h3 style={tituloStyle}>Horario del GPS</h3>
      <p style={subtitleStyle}>Fuera de este horario el GPS de los choferes se apaga, salvo que tengan un recorrido o un Turbo en curso.</p>
      {form ? (
        <>
          <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap", marginTop: "12px" }}>
            <label style={{ display: "flex", gap: "8px", alignItems: "center", minHeight: "36px", color: "var(--text-primary)" }}>
              <input type="checkbox" checked={form.activo} onChange={(e) => cambiar({ activo: e.target.checked })} />
              Apagar el GPS fuera del horario
            </label>
            <label style={labelStyle}>
              Desde
              <input aria-label="Desde" type="time" style={timeStyle} value={form.desde} onChange={(e) => cambiar({ desde: e.target.value })} />
            </label>
            <label style={labelStyle}>
              Hasta
              <input aria-label="Hasta" type="time" style={timeStyle} value={form.hasta} onChange={(e) => cambiar({ hasta: e.target.value })} />
            </label>
            <Button onClick={guardar} disabled={!cambiado || !formato || iguales || saving}>{saving ? "Guardando…" : "Guardar horario"}</Button>
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "10px" }}>
            Hora de Buenos Aires, todos los días.
            {!formato ? " Completá las dos horas." : iguales ? " «Desde» y «hasta» no pueden ser iguales: para no cortar el GPS, destildá el horario." : form.desde > form.hasta ? ` Cruza la medianoche: de las ${form.desde} a las ${form.hasta} del día siguiente.` : ""}
            {form.activo ? "" : " Destildado, el GPS va todo el día."}
          </div>
          {guardada?.updated_at ? (
            <div style={{ ...smallMutedStyle, marginTop: "4px" }}>Último cambio: {fmtDate(guardada.updated_at)}{guardada.updated_by ? `, ${guardada.updated_by}` : ""}</div>
          ) : null}
          {notice ? <div role="status" style={{ ...smallMutedStyle, marginTop: "8px", color: "var(--success, #3fb950)" }}>{notice}</div> : null}
        </>
      ) : !error ? <div style={{ ...smallMutedStyle, marginTop: "12px" }}>Cargando…</div> : null}
      {error ? <div role="alert" style={{ marginTop: "12px", color: "var(--danger)" }}>{error}</div> : null}
    </Card>
  );
}
